import { isMobile } from "./Utils/Mobile.js";
import desktopSources from "./sources.js";
import mobileSources from "./mobilesources.js";

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

// Everything that differs between the desktop and mobile scenes lives here.
// Light and smoke positions are in world units; `room` positions are in the
// GLB's own units (they sit inside ModelGroup, which is scaled by 95).

const desktop = {
  sources: desktopSources,
  renderer: {
    antialias: true,
  },
  postProcessing: true,
  camera: {
    position: [0, 0, 9],
    scrollFactor: 3.5,
  },
  modelGroup: {
    y: -8,
  },
  smoke: {
    position: [-2.05, -4.2, 0.905],
  },
  lights: {
    lamp: {
      color: 0xe8c274,
      intensity: 1.5,
      position: [-1.5, 0.6, -0.7],
      castShadow: true,
    },
    night: {
      color: 0xffd1a6,
      intensity: 1.5,
      position: [2.542, -6.431, -1.637],
      castShadow: true,
    },
    picture: {
      color: 0xe8c274,
      intensity: 1.5,
      position: [4, 1.7, -0.03],
      castShadow: false,
    },
  },
  // Evening falls as the page scrolls. Multipliers reached at the bottom of
  // the page (1 = unchanged), blended in from the top:
  // - fill: ambient light, environment map and picture light
  // - night: the night light (a light source, so it grows)
  // - glow: emissive materials, by material name. "Walls" also carries the
  //   3D title, night-light socket and envelope outline, so it dims less.
  scrollLighting: {
    fill: 0.25,
    night: 1.25,
    glow: { Walls: 0.75, Pictures: 0.5 },
  },
  // Camera glide to a clicked object before its panel opens. The camera ends
  // centred on the object; approach is how far it moves in towards it
  // (fraction of the distance). duration grows for long trips (e.g. from the
  // menu), up to maxDuration.
  flyTo: reducedMotion
    ? null
    : { approach: 0.4, duration: 0.7, maxDuration: 1.3 },
  room: {
    hoverEffects: true,
    wiggle: false,
    receiveShadow: true,
    catToggleEmissive: true,
    catToggleSlideZ: -0.65,
    toggleHitbox: {
      size: [0.003, 0.0065, 0.013],
      position: [-0.025, 0.09724, -0.0406],
    },
    lampShade: { opacity: 0.95, emissiveIntensity: 1 },
    // Glow of the picture icons and cat toggle ("Pictures" material). Below 1
    // on desktop so, with bloom, they don't outshine the lamp.
    pictureGlow: 0.6,
    // The envelope's outline pulses: it's the Contact link at the end of the
    // scroll. Emissive intensity between min and max; speed in radians/s.
    // The outline is already near full brightness at 1, so it has to dim
    // well below 1 for the pulse to be visible.
    envelopePulse: reducedMotion ? null : { min: 0.1, max: 2.5, speed: 2.5 },
    envelopeOffsetY: 0,
  },
};

const mobile = {
  sources: mobileSources,
  renderer: {
    antialias: false,
    stencil: false,
    powerPreference: "high-performance",
  },
  postProcessing: false,
  camera: {
    position: [-0.23, -0.05, 20],
    scrollFactor: 6,
  },
  modelGroup: {
    y: -8.5,
  },
  smoke: {
    position: [-1.6, -9.7, 0.905],
  },
  lights: {
    lamp: {
      color: 0xe8c274,
      intensity: 18,
      position: [1.236, 1.7, -0.7],
      castShadow: false,
    },
    night: {
      color: 0xe8c274,
      intensity: 10,
      position: [1.703, -11.5, -1.637],
      castShadow: false,
    },
    picture: null,
  },
  scrollLighting: {
    fill: 0.25,
    night: 1.5,
    glow: { Walls: 0.75, Pictures: 0.5 },
  },
  flyTo: reducedMotion
    ? null
    : { approach: 0.3, duration: 0.7, maxDuration: 1.3 },
  room: {
    hoverEffects: false,
    wiggle: !reducedMotion,
    receiveShadow: false,
    catToggleEmissive: false,
    catToggleSlideZ: -1.1,
    toggleHitbox: {
      size: [0.003, 0.009, 0.02],
      position: [-0.01, 0.0675, 0.002],
    },
    lampShade: { opacity: 0.9, emissiveIntensity: 2 },
    pictureGlow: 1,
    envelopePulse: reducedMotion ? null : { min: 0.1, max: 2.5, speed: 2.5 },
    envelopeOffsetY: 0.05,
  },
};

export default isMobile() ? mobile : desktop;
